import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { App, Button, Table, Typography } from 'antd';

import { CHECKOUT_ELEMENTS_URL } from 'settings';
import Truncate from 'v2/components/Shared/TruncateText';
import type { ProductOffer } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { Icon } from '@eduzz/ui-antd-theme';

type PricesListProps = {
  isAffiliate: boolean;
  offer: ProductOffer;
};

type ProductPrice = ProductOffer['prices'][0];

function PricesList({ isAffiliate, offer }: PricesListProps) {
  const { message } = App.useApp();
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  function mountOfferLink(price: ProductPrice) {
    const url = new URL(`${CHECKOUT_ELEMENTS_URL}/${offer.key}?currency=${price.currency.code}`);

    if (isAffiliate && user) {
      url.searchParams.append('a', String(user.id));
    }

    return String(url);
  }

  async function copyLinkToClipboard(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      message.success('Link copiado para a área de transferência');
    } catch {
      message.error('Não foi possível copiar o link');
    }
  }

  function mountOfferCurrency(price: ProductPrice) {
    return `${price.currency.code} (${price.currency.name})`;
  }

  if (!offer.prices?.length) return null;

  const columns = [
    {
      title: 'Moeda',
      key: 'currency',
      render: (_: any, price: ProductPrice) => (
        <Typography.Text id={`links-modal-offer-currency-${price.currency.code}`} className='whitespace-nowrap'>
          <Truncate count={25}>{mountOfferCurrency(price)}</Truncate>
        </Typography.Text>
      )
    },
    {
      title: 'Valor',
      key: 'value',
      width: 112,
      render: (_: any, price: ProductPrice) => (
        <Typography.Text id={`links-modal-offer-price-${price.currency.code}`}>
          {price.value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: price.currency.code
          })}
        </Typography.Text>
      )
    },
    {
      title: 'Página de pagamento (Eduzz Checkout Elements)',
      key: 'link',
      width: 300,
      render: (_: any, price: ProductPrice) => (
        <div className='flex items-center gap-2'>
          <Icon.Link size={16} />
          <Typography.Link
            id={`links-modal-offer-link-${price.currency.code}`}
            href={mountOfferLink(price)}
            target='_blank'
            rel='noreferrer'
            ellipsis
          >
            {mountOfferLink(price)}
          </Typography.Link>
          <Button
            type='text'
            id={`links-modal-copy-button-${price.currency.code}`}
            onClick={() => copyLinkToClipboard(mountOfferLink(price))}
            icon={<Icon.Copy size={16} />}
          />
        </div>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={offer.prices}
      rowKey='id'
      pagination={false}
      size='small'
      sticky
      scroll={{ x: 'max-content' }}
    />
  );
}

export { PricesList };
